import { Bibliography } from "../types/bibliography";
import { EntityController } from "./entityController";
import { useNavigate } from "react-router-dom";
import useBibliographyQuery from "../hooks/query/useBibliographyQuery";
import useBibliographyLogic from "../hooks/logic/bibliographyLogic";

/**
 * Extended controller interface for Bibliography with additional helper methods.
 * Extends the unified EntityController interface.
 */
export interface BibliographyController extends EntityController<Bibliography> {
  /** The raw display name without "Edit" or "Create New" prefix */
  displayName: (data: Bibliography) => string;
  /** Title formatted for view pages (just the display name) */
  viewTitle: (data: Bibliography) => string;
  /** Title formatted for edit pages (includes "Edit" or "Create New" prefix) */
  editTitle: (data: Bibliography) => string;
}

/**
 * Combined controller hook for Bibliography entity.
 * Contains all logic needed for both view and edit pages.
 *
 * View pages use: fetchData, viewTitle, userEnums, getEditRoute
 * Edit pages use: fetchData, editTitle, userEnums, onSubmit, onDelete, defaultValues, etc.
 *
 * The unused properties (e.g., mutations on view pages) are initialized but never called.
 */
const useBibliographyController = (
  id: string | null
): BibliographyController => {
  // ============================================
  // BASE LOGIC (shared between view and edit)
  // ============================================
  const { useFetchById, useUpdate, useCreate, useDeleteById } =
    useBibliographyQuery();
  const { userEnums, defaultBibliography, getDisplayName, metadataFieldNames } =
    useBibliographyLogic();
  const fetchById = useFetchById(id || "");
  const navigate = useNavigate();

  const fetchData = async (): Promise<Bibliography> => {
    if (!id) {
      return defaultBibliography;
    }
    const res = await fetchById.refetch();
    const bibliographyData = res.data;
    if (!bibliographyData || res.isError) {
      throw new Error("Bibliography data not found");
    }
    return bibliographyData;
  };

  // ============================================
  // TITLE FUNCTIONS
  // ============================================
  const displayName = (data: Bibliography) => getDisplayName(data);

  const viewTitle = (data: Bibliography) =>
    data.title || "Untitled Bibliography";

  const isCreateMode = id === null;

  const editTitle = (data: Bibliography) =>
    `${isCreateMode ? "Create New" : "Edit"} ${displayName(data)}`;

  // ============================================
  // EDIT-SPECIFIC LOGIC (mutations)
  // ============================================
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();

  const onSubmit = async (data: Partial<Bibliography>) => {
    const updateData: Partial<Bibliography> = {
      internal_notes: data.internal_notes,
      notes: data.notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      filename: data.filename,
      author: data.author,
      title: data.title,
      volumes: data.volumes,
      year: data.year,
      sanskritof: data.sanskritof,
      sans_complete: data.sans_complete,
      sans_tohoku: data.sans_tohoku,
      sans_otani: data.sans_otani,
      tibetanof: data.tibetanof,
      tibe_complete: data.tibe_complete,
      tibe_tohoku: data.tibe_tohoku,
      tibe_otani: data.tibe_otani,
      journal: data.journal,
      startpage: data.startpage,
      endpage: data.endpage,
      booktitle: data.booktitle,
      bookeditor: data.bookeditor,
      seriesname: data.seriesname,
      seriesnumber: data.seriesnumber,
      serieseditor: data.serieseditor,
      publicationplace: data.publicationplace,
      publisher: data.publisher,
      source: data.source,
      jababo: data.jababo,
      no_of_volumes: data.no_of_volumes,
      volume_no: data.volume_no,
      attributes_metadata: {
        ...defaultBibliography.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update bibliography");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create bibliography");
      }
    }
    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Bibliography ID is required for deletion");
    }
    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      throw new Error("Failed to delete bibliography");
    }
  };

  const navigateBack = () => navigate(-1);

  const deleteWarningMessage =
    "Are you sure you want to delete this bibliography?";

  // ============================================
  // VIEW-SPECIFIC LOGIC
  // ============================================
  const getEditRoute = () => `/bibliographies/${id}/edit`;

  // ============================================
  // RETURN COMBINED CONTROLLER
  // ============================================
  return {
    // Base (shared)
    fetchData,
    userEnums,

    // Title functions
    displayName,
    viewTitle,
    editTitle,
    title: viewTitle, // Default is view-friendly (no prefix)

    // Edit-specific
    defaultValues: defaultBibliography,
    metadataFieldNames,
    isCreateMode,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,

    // View-specific
    getEditRoute,
  };
};

export default useBibliographyController;
